import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { SaveDeviceForm, useDeleteDeviceMutation } from '@/hooks/connectNewDevice';
import { useToast } from '@/hooks/use-toast';
import { DeleteDeviceInput, DeleteDeviceMut } from '@/lib/api/devices/devices';
import { queryClient } from '@/lib/queryClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

/** @deprecated */
export default function DeleteDeviceModel({
  deviceData,
  id,
}: {
  id: string;
  deviceData: SaveDeviceForm;
}) {
  const { mutateAsync: deleteAsync } = useDeleteDeviceMutation({ id, queryClient });
  const navigate = useNavigate();
  const { toast } = useToast();

  async function deleteDevice() {
    await deleteAsync(deviceData)
      .then(() => {
        toast({ title: 'Deleted Device' });
        navigate('/devices');
      })
      .catch(() => {
        toast({ title: 'Error deleting device', variant: 'destructive' });
      });
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={'destructive'}>Permanently Delete Device</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete the device, and it's and
            remove your data from our servers.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row flex-wrap justify-between">
          <DialogClose asChild>
            <Button variant={'ghost'}>Cancel</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button onClick={deleteDevice} variant={'destructive'}>
              Confirm
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type DeleteDeviceButtonProps = {
  id: string;
  getInput: () => Omit<DeleteDeviceInput, 'deviceId'> | undefined;
  onSuccess?: () => void;
};

export const DeleteDeviceButton = ({ id, getInput, onSuccess }: DeleteDeviceButtonProps) => {
  const queryClient = useQueryClient();
  const deleteMut = useMutation(DeleteDeviceMut(id));
  const navigate = useNavigate();
  const { toast } = useToast();

  const deleteDevice = async () => {
    const input = getInput();
    if (input == null) return;

    try {
      await deleteMut.mutateAsync(input);
      toast({ title: 'Deleted Device' });
      queryClient.invalidateQueries({
        queryKey: ['devices.search'],
      });
      queryClient.invalidateQueries({
        queryKey: ['siteDevices'],
      });
      queryClient.invalidateQueries({
        queryKey: ['siteAssets'],
      });

      if (onSuccess) {
        onSuccess();
        return;
      }

      navigate(-1);
    } catch (e) {
      toast({ title: 'Error deleting device', variant: 'destructive' });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={'destructive'}>Permanently Delete Device</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete the device, and it's and
            remove your data from our servers.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row flex-wrap justify-between">
          <DialogClose asChild>
            <Button variant={'ghost'}>Cancel</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button onClick={deleteDevice} variant={'destructive'}>
              Confirm
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
